import React, { useEffect, useMemo, useState } from 'react'

import Modal from '../reusable/modal'
import { usePet } from '../../modules'

const defaultDeviceOs = typeof navigator !== 'undefined'
    ? `${navigator.platform || 'browser'}`
    : 'browser'

const ConsentOtp = ({ visible, patient, scheme, admissionId, orderId, visitId, mode = 'visit-start', eligibilitySummary, overrideSummary, onSuccess, onCancel }) => {
    const [session, setSession] = useState()
    const [code, setCode] = useState('')
    const [message, setMessage] = useState()
    const [consentMethod, setConsentMethod] = useState('otp')
    const [consentOptions, setConsentOptions] = useState([])
    const [agentId, setAgentId] = useState('')
    const [workStationId, setWorkStationId] = useState(() => sessionStorage.getItem('station') || localStorage.getItem('station') || '')
    const [authorizingDeviceOs, setAuthorizingDeviceOs] = useState(defaultDeviceOs)

    const biometricEnabled = useMemo(
        () => consentOptions.some(option => option?.value === 'biometric' && option?.enabled),
        [consentOptions],
    )

    const canStartBiometricSession = Boolean(agentId.trim() && workStationId.trim())

    const commonPayload = {
        patient: patient?._id,
        scheme: scheme?._id,
        admission: admissionId,
        order: orderId,
        visit: visitId,
        mode,
        eligibilityStatus: eligibilitySummary?.status,
        eligibilityCheckedAt: eligibilitySummary?.timestamp,
        eligibilityPayload: eligibilitySummary,
        overrideStatus: overrideSummary?.status,
        overrideReason: overrideSummary?.reason,
    }

    const [, fetchConsentOptions] = usePet('claimConsentOptions', commonPayload, {
        notify: false,
        manual: true,
        callback: ({ data }) => {
            const methods = data?.methods ?? [{ value: 'otp', label: 'OTP', enabled: true }]
            setConsentOptions(methods)
            const preferred = data?.defaults?.consentMethod
            const fallback = methods.find(option => option?.enabled)?.value ?? 'otp'
            setConsentMethod(preferred && methods.some(option => option?.value === preferred && option?.enabled) ? preferred : fallback)
        }
    })

    const [{ loading: sending }, sendOtp] = usePet('sendClaimOtp', {
        ...commonPayload,
        consentMethod,
        ...(consentMethod === 'biometric'
            ? {
                agentId,
                workStationId,
                authorizingDeviceOs,
            }
            : {}),
    }, {
        notify: true,
        manual: true,
        load: { msg: consentMethod === 'biometric' ? 'Starting biometric authorization...' : 'Sending OTP...' },
        callback: ({ data }) => {
            setSession(data)
            setMessage(null)
        }
    })

    const [{ loading: validating }, validateOtp] = usePet('validateClaimOtp', {
        session: session?.session_id,
        code,
    }, {
        notify: true,
        manual: true,
        load: { msg: consentMethod === 'biometric' ? 'Confirming biometric authorization...' : 'Validating OTP...' },
        callback: ({ data }) => onSuccess(data)
    })

    useEffect(() => {
        if (!visible || !patient?._id || (!scheme?._id && !admissionId && !orderId && !visitId)) return

        setSession(null)
        setCode('')
        setMessage(null)
        fetchConsentOptions()
    }, [visible, patient?._id, scheme?._id, admissionId, orderId, visitId, mode]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!visible || !patient?._id || (!scheme?._id && !admissionId && !orderId && !visitId)) return

        setSession(null)
        setCode('')
        if (consentMethod === 'biometric') {
            setMessage('Enter the biometric workstation details, then start the authorization session.')
            return
        }

        setMessage(null)
        sendOtp({
            ...commonPayload,
            consentMethod,
            ...(consentMethod === 'biometric'
                ? {
                    agentId,
                    workStationId,
                    authorizingDeviceOs,
                }
                : {}),
        })
    }, [consentMethod, visible, patient?._id, scheme?._id, admissionId, orderId, visitId, mode]) // eslint-disable-line react-hooks/exhaustive-deps

    const startBiometricSession = ({ force = false, announce = false } = {}) => {
        if (!canStartBiometricSession || consentMethod !== 'biometric') return

        setCode('')
        setSession(null)
        setMessage(announce ? 'A new biometric authorization session has been requested.' : null)
        sendOtp({
            ...commonPayload,
            consentMethod,
            ...(force ? { force: true } : {}),
            agentId,
            workStationId,
            authorizingDeviceOs,
        })
    }

    const resend = () => {
        if (consentMethod === 'biometric') {
            startBiometricSession({ force: true, announce: true })
            return
        }

        setCode('')
        setMessage('A new code has been requested.')
        sendOtp({
            ...commonPayload,
            consentMethod,
            force: true,
        })
    }

    const canValidate = consentMethod === 'biometric'
        ? Boolean(session?.session_id)
        : Boolean(session?.session_id) && code.trim().length >= 4

    const biometricLaunchUrl = session?.biometric_iframe_url
        ? session.biometric_iframe_url
        : null

    return (
        <Modal
            w={700}
            title={`Consent Confirmation (${consentMethod === 'biometric' ? 'Biometric' : 'OTP'})`}
            toggle={onCancel}
            visible={visible}>
            <div className="space-y-4">
                <div>
                    <p className="text-sm font-medium mb-1">{patient?.name}</p>
                    <p className="text-sm text-gray-600 mb-0">
                        {mode === 'discharge'
                            ? `We are capturing the final ${consentMethod === 'biometric' ? 'biometric authorization' : 'confirmation code'} before closing this visit.`
                            : `We are capturing ${consentMethod === 'biometric' ? 'biometric authorization' : 'a confirmation code'} to authorize this visit.`}
                    </p>
                </div>

                {
                    biometricEnabled &&
                    <div className="rounded-sm border border-gray-200 px-4 py-3">
                        <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Consent Method</p>
                        <div className="flex flex-wrap gap-2">
                            {
                                consentOptions.filter(option => option?.enabled).map(option =>
                                    <button
                                        key={ option.value }
                                        onClick={ () => setConsentMethod(option.value) }
                                        className={ `btn ${consentMethod === option.value ? 'bg-emerald-600 text-white' : 'bg-white border border-gray-300 hover:bg-gray-50'}` }>
                                        {option.label}
                                    </button>
                                )
                            }
                        </div>
                    </div>
                }

                {
                    overrideSummary?.reason &&
                    <div className="rounded-sm border border-yellow-200 bg-yellow-50 px-4 py-3">
                        <p className="text-xs text-yellow-800 uppercase font-semibold mb-1">Manual eligibility override</p>
                        <p className="text-sm text-yellow-900 mb-1">{overrideSummary.reason}</p>
                        <p className="text-xs text-yellow-800 mb-0">
                            Prior eligibility status: {overrideSummary.status ?? 'unknown'}
                        </p>
                    </div>
                }

                {
                    consentMethod === 'biometric' &&
                    <div className="grid gap-4 md:grid-cols-2">
                        <div>
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">Biometric Agent ID</label>
                            <input
                                type="text"
                                value={agentId}
                                onChange={e => setAgentId(e.target.value)}
                                placeholder="National ID of biometric agent"
                                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                            />
                        </div>
                        <div>
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">Workstation ID</label>
                            <input
                                type="text"
                                value={workStationId}
                                onChange={e => setWorkStationId(e.target.value)}
                                placeholder="Biometric workstation ID"
                                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">Authorizing Device OS</label>
                            <input
                                type="text"
                                value={authorizingDeviceOs}
                                onChange={e => setAuthorizingDeviceOs(e.target.value)}
                                placeholder="windows"
                                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                            />
                        </div>
                    </div>
                }

                <div className="rounded-sm border border-gray-200 px-4 py-3">
                    <p className="text-xs text-gray-500 uppercase font-semibold mb-1">{consentMethod === 'biometric' ? 'Authorization Status' : 'OTP Status'}</p>
                    <p className="text-sm mb-0">
                        {
                            consentMethod === 'biometric'
                                ? session?.biometric_status
                                    ? `Biometric session ${session.biometric_status}`
                                    : sending
                                        ? 'Starting biometric session...'
                                        : 'Waiting to start biometric session...'
                                : session?.phone_number
                                    ? `Code sent to ${session.phone_number}`
                                    : sending
                                        ? 'Sending code...'
                                        : 'Waiting to send code...'
                        }
                    </p>
                    {session?.expires_at && <p className="text-xs text-gray-500 mb-0 mt-1">Expires at {new Date(session.expires_at).toLocaleTimeString()}</p>}
                    {consentMethod === 'biometric' && session?.biometric_iframe_url && (
                        <div className="mt-3 rounded-sm border border-indigo-200 bg-indigo-50 px-3 py-3">
                            <p className="text-sm text-indigo-800 mb-2">Use the SHA biometric/eKYC session below to complete authorization.</p>
                            <div className="rounded-sm overflow-hidden border border-indigo-200 bg-white mb-2">
                                <iframe
                                    title="SHA biometric authorization"
                                    src={biometricLaunchUrl}
                                    className="w-full"
                                    style={{ minHeight: 420, border: 0 }}
                                />
                            </div>
                            <a
                                href={biometricLaunchUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs text-indigo-700 underline break-all">
                                Open biometric session in a new tab
                            </a>
                        </div>
                    )}
                </div>

                {
                    consentMethod === 'otp' &&
                    <div>
                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-otp-code">
                            Enter OTP Code
                        </label>
                        <input
                            id="grid-otp-code"
                            type="text"
                            value={code}
                            maxLength={6}
                            onChange={e => setCode(e.target.value)}
                            placeholder="123456"
                            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                        />
                    </div>
                }

                {message && <p className="text-xs text-blue-600 mb-0">{message}</p>}

                <div className="flex justify-end gap-2">
                    <button onClick={onCancel} className="btn bg-white border border-gray-300 hover:bg-gray-50">
                        Cancel
                    </button>
                    <button onClick={resend} disabled={sending || (consentMethod === 'biometric' && (!agentId.trim() || !workStationId.trim()))} className="btn bg-white border border-gray-300 hover:bg-gray-50">
                        {consentMethod === 'biometric' ? 'Restart Session' : 'Resend OTP'}
                    </button>
                    {
                        consentMethod === 'biometric' &&
                        <button
                            onClick={ () => startBiometricSession() }
                            disabled={sending || !canStartBiometricSession}
                            className="btn bg-white border border-gray-300 hover:bg-gray-50">
                            Start Session
                        </button>
                    }
                    <button
                        onClick={validateOtp}
                        disabled={validating || sending || !canValidate}
                        className="text-white hisani-btn-green-light font-medium rounded-lg text-sm px-5 py-2.5 text-center">
                        {consentMethod === 'biometric' ? 'Confirm Authorization' : 'Validate Code'}
                    </button>
                </div>
            </div>
        </Modal>
    )
}

export default ConsentOtp
